/* eslint-disable indent-legacy */
/** @prettier */
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { Fragment, useRef, useState } from 'react';

import ChildrenPropsContext from '@xanadu/ui-shared/contexts/ChildrenPropsContext';
import { useReduxBridge } from '@xanadu/ui-shared/contexts/ReduxBridgeContext';
import { useImprovedTranslation } from '@xanadu/ui-shared/hooks';

import { useClickOutsideCallback } from '../../../../hooks';

import { getClientIcons } from '../../../../utils';

import ArrowIcon from '../../../../../../images/icons/misc/Arrow-Down.svg';
import LogoutIcon from '../../../../../../images/icons/misc/logout_mod.svg';

import LanguageSelector from './LanguageSelector';
import OddsTypeSelector from './OddsTypeSelector';
import styles from './SettingsDrop.module.scss';

const SettingsDrop = ({ extraClasses, isLoggedIn, logoutFn }) => {
  const [entering, setEntering] = useState(false);
  const [leaving, setLeaving] = useState(false);

  const [isMenuShown, setIsMenuShown] = useState(false);

  const menuRef = useRef();
  const buttonRef = useRef();

  const {
    t,
    i18n: { language: activeLanguage },
  } = useImprovedTranslation();

  const { clientName, clientSite } = useReduxBridge() || {};
  const {
    availableLanguages = [],
    childrenProps: {
      SettingsDrop: { displayLanguageSelector = false, displayOddsTypeSelector = false } = {},
    } = {},
  } = React.useContext(ChildrenPropsContext) || {};

  const isSingleLanguage = availableLanguages.length === 1;

  const {
    LanguageSelectorIcon = () => null,
    SettingsIcon = () => null,
  } = getClientIcons(clientName, clientSite, activeLanguage) || {};

  const handleClick = () => {
    setIsMenuShown((prev) => !prev);
  };

  useClickOutsideCallback({
    refs: [buttonRef, menuRef],
    callback: handleClick,
  });

  const settingsIconClass = classNames({
    [styles.iconStyle]: true,
    [styles.rotateEnter]: entering,
    [styles.rotateExit]: leaving,
    [styles['iconStyle--wider']]: isSingleLanguage
  });

  const dropWithExtraClasses = classNames({
    [styles.dropMenu]: true,
    [extraClasses]: !!extraClasses,
  });

  const moreThanOneLanguage = availableLanguages.length > 1;

  const displayLSelector = moreThanOneLanguage && displayLanguageSelector;

  return (
    <React.Fragment>
      <div
        role="button"
        ref={buttonRef}
        className={styles.settingsButton}
        onMouseEnter={
          !moreThanOneLanguage
            ? () => {
              setEntering(true);
              setLeaving(false);
            }
            : undefined
        }
        onMouseLeave={
          !moreThanOneLanguage
            ? () => {
              setLeaving(true);
              setEntering(false);
            }
            : undefined
        }
        onClick={handleClick}
      >
        {isSingleLanguage ?
          <SettingsIcon className={settingsIconClass} />
          : (
            <Fragment>
              <LanguageSelectorIcon className={settingsIconClass} />
              <ArrowIcon />
            </Fragment>
          )}
      </div>
      {isMenuShown ? (
        <div className={dropWithExtraClasses} ref={menuRef}>
          {displayLSelector ? <LanguageSelector /> : null}
          {displayLSelector && displayOddsTypeSelector ? <div style={{ marginTop: 16 }} /> : null}
          {displayOddsTypeSelector ? <OddsTypeSelector /> : null}
          {isLoggedIn ? (
            <div role="button" onClick={logoutFn} className={styles.logoutButton}>
              {t('Logout')}
              <LogoutIcon />
            </div>
          ) : null}
        </div>
      ) : null}
    </React.Fragment>
  );
};

SettingsDrop.propTypes = {
  extraClasses: PropTypes.string,
  isLoggedIn: PropTypes.bool,
  logoutFn: PropTypes.func.isRequired,
};

export default SettingsDrop;
